import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import styled from "styled-components";
import CountryFlag from "../../components/CountryFlag";
import Button from "../../components/Button/Button";
import InputText from "../../components/Input/InputText";
import InputTextArea from "../../components/Input/InputTextArea";
import Select from "../../components/Select/Select";
import Typography from "../../components/Typography";
import countryCodes from "../../utils/CountryCodes";
import { getPlatform } from "../../utils/socialMediaUtils";
import { theme } from "../../theme";
import EditIcon from "../../components/Icons/EditIcon";
import CheckIcon from "../../components/Icons/CheckIcon";
import HelmetIcon from "../../components/Icons/HelmetIcon";
import UploadFileIcon from "../../components/Icons/UploadFileIcon";
import DeleteOutlineIcon from "../../components/Icons/DeleteOutlineIcon";

const EditProfile = () => {
	const { t } = useTranslation("userProfile");
	const navigate = useNavigate();
	const location = useLocation();
	const { user, refreshUserData } = useAuth();
	const [formData, setFormData] = useState({
		name: "",
		lastName: "",
		userAvatar: "",
		description: "",
		address: "",
		locality: "",
		country: "",
		phonePrefix: "+34",
		phoneNumber: "",
		socialMediaLinks: [],
	});

	const [file, setFile] = useState(null);
	const [newLink, setNewLink] = useState("");
	const [errors, setErrors] = useState({});
	const [editingIndex, setEditingIndex] = useState(null);
	const [editingField, setEditingField] = useState(null);
	const [tempValues, setTempValues] = useState({ url: "", title: "" });
	const [isLoading, setIsLoading] = useState(false);
	const editRef = useRef(null);
	const formRef = useRef(null);

	// Derived state for selected country
	const selectedCountry = countryCodes.find(
		(code) => code.dial_code === formData.phonePrefix,
	);

	// Cargar datos del usuario al montar el componente
	useEffect(() => {
		if (user) {
			setFormData({
				name: user.name || "",
				lastName: user.lastName || "",
				userAvatar: user.userAvatar || "",
				description: user.description || "",
				address: user.address || "",
				locality: user.locality || "",
				country: user.country || "",
				phonePrefix: user.phonePrefix || "+34",
				phoneNumber: user.phoneNumber || "",
				socialMediaLinks: user.socialMediaLinks || [],
			});
		}
	}, [user]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		if (errors[name]) {
			setErrors((prev) => ({
				...prev,
				[name]: "",
			}));
		}
	};

	const handleFileChange = (e) => {
		const selectedFile = e.target.files[0];
		if (selectedFile) {
			setFile(selectedFile);
			const reader = new FileReader();
			reader.onload = (event) => {
				setFormData((prev) => ({
					...prev,
					userAvatar: event.target.result,
				}));
			};
			reader.readAsDataURL(selectedFile);
		}
	};

	const triggerFileInput = () => {
		document.getElementById("file").click();
	};

	const validateForm = () => {
		const newErrors = {};

		if (!formData.name.trim()) {
			newErrors.name = t("errors.nameRequired");
		}

		if (!formData.lastName.trim()) {
			newErrors.lastName = t("errors.lastNameRequired");
		}

		if (formData.phoneNumber && !/^\d+$/.test(formData.phoneNumber)) {
			newErrors.phoneNumber = t("errors.invalidPhoneNumber");
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const navigateBackToSettings = () => {
		if (location.state?.fromSettings) {
			navigate(-1);
			return;
		}

		if (location.state?.returnTo) {
			navigate(
				location.state.returnTo,
				location.state.returnToState
					? { state: location.state.returnToState }
					: undefined,
			);
			return;
		}

		navigate(-1);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsLoading(true);

		try {
			const formDataToSend = new FormData();

			Object.keys(formData).forEach((key) => {
				if (["address", "locality", "country"].includes(key)) return;
				if (key === "socialMediaLinks") {
					formDataToSend.append(key, JSON.stringify(formData[key]));
				} else {
					formDataToSend.append(key, formData[key]);
				}
			});

			// Agregar archivo si existe
			if (file) {
				formDataToSend.append("userAvatar", file);
			}

			const response = await axiosClient.put(
				"/api/users/profile",
				formDataToSend,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			);

			if (response.data.success) {
				toast.success(t("messages.profileUpdated"));
				await refreshUserData();
				navigateBackToSettings();
			}
		} catch (error) {
			console.error("Error updating profile:", error);
			toast.error(t("errors.updateFailed"));
		} finally {
			setIsLoading(false);
		}
	};

	const startEditing = (index, field, currentValue) => {
		setEditingIndex(index);
		setEditingField(field);
		setTempValues({ ...tempValues, [field]: currentValue });

		// Focus and position cursor at the end after state update
		setTimeout(() => {
			const input = document.querySelector('input[type="text"]:focus');
			if (input) {
				const length = input.value.length;
				input.setSelectionRange(length, length);
			}
		}, 0);
	};

	const saveEdit = () => {
		const updatedLinks = [...formData.socialMediaLinks];
		updatedLinks[editingIndex] = {
			...updatedLinks[editingIndex],
			[editingField]: tempValues[editingField],
			platform:
				editingField === "url"
					? getPlatform(tempValues.url)
					: updatedLinks[editingIndex].platform,
		};

		setFormData((prev) => ({
			...prev,
			socialMediaLinks: updatedLinks,
		}));

		setEditingIndex(null);
		setEditingField(null);
		setTempValues({ url: "", title: "" });
	};

	const cancelEdit = () => {
		setEditingIndex(null);
		setEditingField(null);
		setTempValues({ url: "", title: "" });
	};

	const extractTitleFromUrl = (url) => {
		try {
			const urlObj = new URL(url);
			const hostname = urlObj.hostname.replace("www.", "");
			const pathSegments = urlObj.pathname
				.split("/")
				.filter((segment) => segment);

			// For social media platforms, try to extract meaningful title
			if (hostname.includes("twitter.com") || hostname.includes("x.com")) {
				return pathSegments[0] ? `@${pathSegments[0]}` : hostname;
			}
			if (hostname.includes("instagram.com")) {
				return pathSegments[0] ? `@${pathSegments[0]}` : hostname;
			}
			if (hostname.includes("linkedin.com")) {
				return pathSegments[1] ? pathSegments[1] : hostname;
			}
			if (hostname.includes("github.com")) {
				return pathSegments[0] ? pathSegments[0] : hostname;
			}

			// Default: use hostname
			return hostname;
		} catch {
			return url;
		}
	};

	const handleAddLink = () => {
		if (newLink.trim()) {
			const platform = getPlatform(newLink);
			const title = extractTitleFromUrl(newLink);

			const isDuplicate = formData.socialMediaLinks.some(
				(link) => link.url.toLowerCase() === newLink.toLowerCase(),
			);

			if (isDuplicate) {
				toast.error(t("messages.duplicateLink"));
				return;
			}

			setFormData((prevState) => ({
				...prevState,
				socialMediaLinks: [
					...prevState.socialMediaLinks,
					{ url: newLink, platform, title },
				],
			}));
			setNewLink("");
		}
	};

	const handleRemoveLink = (index) => {
		const updatedLinks = formData.socialMediaLinks.filter(
			(_, i) => i !== index,
		);
		setFormData({ ...formData, socialMediaLinks: updatedLinks });
	};

	return (
		<>
			<Navigation>
				<Container>
					<Typography
						as="h3"
						$variant="title-5-semibold"
						$variantTablet="body-1-semibold"
						$variantMobile="body-1-semibold"
						color={theme.colors.defaultStrong}
					>
						{t("editProfile.title")}
					</Typography>
					<ActionsBlocks>
						<Button
							$variant="outline"
							size="l"
							sizeTablet="l"
							sizeMobile="m"
							onClick={navigateBackToSettings}
						>
							{t("editProfile.cancel")}
						</Button>
						<Button
							type="button"
							size="l"
							sizeTablet="l"
							sizeMobile="m"
							disabled={isLoading}
							onClick={() => {
								if (formRef.current) {
									formRef.current.requestSubmit();
								}
							}}
						>
							{isLoading ? t("editProfile.saving") : t("editProfile.save")}
						</Button>
					</ActionsBlocks>
				</Container>
			</Navigation>
			<EditProfileContainer>
				<PersonalDetailsContainer>
					{/* Introducción */}

					<Form ref={formRef} onSubmit={handleSubmit}>
						<InnerForm>
							{/* Avatar Section */}
							<UserAvatarRow>
								{file ? (
									<ImageWrapper
										onClick={triggerFileInput}
										style={{ cursor: "pointer" }}
									>
										<AvatarImage
											src={URL.createObjectURL(file)}
											alt="User Avatar"
										/>
									</ImageWrapper>
								) : formData.userAvatar ? (
									<ImageWrapper
										onClick={triggerFileInput}
										style={{ cursor: "pointer" }}
									>
										<AvatarImage src={formData.userAvatar} alt="User Avatar" />
									</ImageWrapper>
								) : (
									<EmptyImageWrapper
										onClick={triggerFileInput}
										style={{ cursor: "pointer" }}
									>
										{/* <EmptyAvatarImage src="/icons/helmet.svg" /> */}
										<HelmetIcon
											width={32}
											height={32}
											fill={theme.colors.defaultStrong}
										/>
									</EmptyImageWrapper>
								)}
								<UploadField>
									<UploadAvatarButton>
										<UploadFileIcon
											width={16}
											height={16}
											fill={theme.colors.defaultStrong}
										/>
										{/* <img src="/icons/upload-file.svg" alt={t('form.userAvatar.uploadImage')} /> */}
										<Typography $variant="body-1-semibold">
											{t("editProfile.form.userAvatar.uploadImage")}
										</Typography>
									</UploadAvatarButton>
									<InputFile
										type="file"
										id="file"
										onChange={handleFileChange}
										accept="image/*"
									/>
								</UploadField>
								{errors.userAvatar && <ErrorMsg>{errors.userAvatar}</ErrorMsg>}
							</UserAvatarRow>

							{/* Campos del formulario */}
							{/* Name and Last Name */}
							<FormRow>
								<NameInputWrapper>
									<Typography $variant="body-2-medium">
										{t("editProfile.form.name.label")}
									</Typography>
									<InputText
										type="text"
										name="name"
										value={formData.name}
										onChange={handleInputChange}
										$size=""
										placeholder={t("editProfile.form.name.placeholder")}
										$variant={errors.name ? "error" : ""}
										required
									/>
									{errors.name && <ErrorMsg>{errors.name}</ErrorMsg>}
								</NameInputWrapper>
								<LastNameInputWrapper>
									<Typography $variant="body-2-medium">
										{t("editProfile.form.lastName.label")}
									</Typography>
									<InputText
										type="text"
										name="lastName"
										value={formData.lastName}
										onChange={handleInputChange}
										$size=""
										placeholder={t("editProfile.form.lastName.placeholder")}
										$variant={errors.lastName ? "error" : ""}
										required
									/>
									{errors.lastName && <ErrorMsg>{errors.lastName}</ErrorMsg>}
								</LastNameInputWrapper>
							</FormRow>

							{/* Phone */}
							<FormRow>
								<PhonePrefixInputWrapper>
									<Typography $variant="body-2-medium">
										{t("editProfile.form.phonePrefix.label")}
									</Typography>
									<PhonePrefixInput>
										{selectedCountry && (
											<PrefixFlag
												countryCode={selectedCountry.code}
												height="20"
											/>
										)}
										<PrefixSelect
											name="phonePrefix"
											value={formData.phonePrefix}
											onChange={handleInputChange}
											$size=""
											$variant={errors.phonePrefix ? "error" : ""}
											required
											autoComplete="off"
										>
											{countryCodes.map((code) => (
												<option
													key={`${code.dial_code}-${code.code}`}
													value={code.dial_code}
												>
													{code.name} ({code.dial_code})
												</option>
											))}
										</PrefixSelect>
									</PhonePrefixInput>
									{errors.phonePrefix && (
										<ErrorMsg>{errors.phonePrefix}</ErrorMsg>
									)}
								</PhonePrefixInputWrapper>
								<PhoneInputWrapper>
									<Typography $variant="body-2-medium">
										{t("editProfile.form.phoneNumber.label")}
									</Typography>
									<InputText
										type="text"
										name="phoneNumber"
										value={formData.phoneNumber}
										onChange={handleInputChange}
										$size=""
										placeholder={t("editProfile.form.phoneNumber.placeholder")}
										$variant={errors.phoneNumber ? "error" : ""}
										// required
										autoComplete="off"
									/>
									{errors.phoneNumber && (
										<ErrorMsg>{errors.phoneNumber}</ErrorMsg>
									)}
								</PhoneInputWrapper>
							</FormRow>

							{/* Biography */}
							<FormRow>
								<InputTextAreaWrapper>
									<Typography $variant="body-2-medium">
										{t("editProfile.form.biography.label")}
									</Typography>
									<InputTextArea
										style={{ fieldSizing: "content" }}
										name="description"
										value={formData.description}
										onChange={handleInputChange}
										$size=""
										autoComplete="off"
									/>
								</InputTextAreaWrapper>
							</FormRow>

							{/* Social Media Links */}
							<SocialMediaLinksRow>
								<InputLinksWrapper>
									<Typography $variant="body-2-medium">
										{t("editProfile.form.socialMedia.label")}
									</Typography>
									<LinksInputInnerWrapper>
										<InputText
											type="text"
											name="socialMediaLink"
											value={newLink}
											onChange={(e) => setNewLink(e.target.value)}
											placeholder={t(
												"editProfile.form.socialMedia.linkPlaceholder",
											)}
											$size=""
										/>
										<Button
											$variant="outline"
											size="l"
											type="button"
											style={{ alignSelf: "stretch" }}
											onClick={handleAddLink}
										>
											{t("editProfile.form.socialMedia.addLink")}
										</Button>
									</LinksInputInnerWrapper>
								</InputLinksWrapper>
								{formData.socialMediaLinks.length > 0 && (
									<LinksList>
										{formData.socialMediaLinks.map((link, index) => {
											const isEditing = editingIndex === index;

											return (
												<SocialLinkItem
													key={index}
													ref={isEditing ? editRef : null}
												>
													<LinkContentWrapper>
														<LinkDetailsWrapper>
															{/* URL Field */}
															<LinkFieldWrapper>
																{isEditing && editingField === "url" ? (
																	<EditInputWrapper>
																		<LinkValue>
																			<LinkLabel
																				as="p"
																				$variant="body-2-medium"
																				color={theme.colors.defaultWeak}
																			>
																				{t(
																					"editProfile.form.socialMedia.urlLabel",
																				)}
																			</LinkLabel>
																			<EditInput
																				type="text"
																				value={tempValues.url}
																				onChange={(e) =>
																					setTempValues({
																						...tempValues,
																						url: e.target.value,
																					})
																				}
																				onKeyDown={(e) => {
																					if (e.key === "Enter") saveEdit();
																					if (e.key === "Escape") cancelEdit();
																				}}
																				autoFocus
																			/>
																		</LinkValue>
																		<Button
																			$variant="ghost"
																			$size=""
																			$iconOnly
																			onClick={saveEdit}
																			title={t(
																				"editProfile.socialMedia.saveChanges",
																			)}
																		>
																			<CheckIcon
																				fill={theme.colors.successMain}
																				width="16px"
																				height="16px"
																			/>
																		</Button>
																	</EditInputWrapper>
																) : (
																	<LinkValueWrapper>
																		<LinkValue>
																			<LinkLabel
																				as="p"
																				$variant="body-2-medium"
																				color={theme.colors.defaultWeak}
																			>
																				{t(
																					"editProfile.form.socialMedia.urlLabel",
																				)}
																			</LinkLabel>
																			<LinkText
																				as="p"
																				$variant="body-1-regular"
																				color={theme.colors.defaultWeak}
																			>
																				{link.url}
																			</LinkText>
																		</LinkValue>
																		<Button
																			$variant="ghost"
																			$size=""
																			$iconOnly
																			onClick={() =>
																				startEditing(index, "url", link.url)
																			}
																			title={t(
																				"editProfile.form.socialMedia.editUrl",
																			)}
																		>
																			<EditIcon
																				fill={theme.colors.defaultStrong}
																				width="16px"
																				height="16px"
																			/>
																		</Button>
																	</LinkValueWrapper>
																)}
															</LinkFieldWrapper>

															{/* Title Field */}
															<LinkFieldWrapper>
																{isEditing && editingField === "title" ? (
																	<EditInputWrapper>
																		<LinkValue>
																			<LinkLabel
																				as="p"
																				$variant="body-2-medium"
																				color={theme.colors.defaultWeak}
																			>
																				{t(
																					"editProfile.form.socialMedia.titleLabel",
																				)}
																			</LinkLabel>
																			<EditInput
																				type="text"
																				value={tempValues.title}
																				onChange={(e) =>
																					setTempValues({
																						...tempValues,
																						title: e.target.value,
																					})
																				}
																				onKeyDown={(e) => {
																					if (e.key === "Enter") saveEdit();
																					if (e.key === "Escape") cancelEdit();
																				}}
																				autoFocus
																			/>
																		</LinkValue>
																		<Button
																			$variant="ghost"
																			$size=""
																			$iconOnly
																			onClick={saveEdit}
																			title={t(
																				"editProfile.form.socialMedia.editTitle",
																			)}
																		>
																			<CheckIcon
																				fill={theme.colors.successMain}
																				width="16px"
																				height="16px"
																			/>
																		</Button>
																	</EditInputWrapper>
																) : (
																	<LinkValueWrapper>
																		<LinkValue>
																			<LinkLabel
																				as="p"
																				$variant="body-2-medium"
																				color={theme.colors.defaultWeak}
																			>
																				{t(
																					"editProfile.form.socialMedia.titleLabel",
																				)}
																			</LinkLabel>
																			<LinkTitle
																				as="p"
																				$variant="body-1-regular"
																				color={theme.colors.defaultWeak}
																			>
																				{link.title ||
																					extractTitleFromUrl(link.url)}
																			</LinkTitle>
																		</LinkValue>
																		<Button
																			$variant="ghost"
																			$size=""
																			$iconOnly
																			onClick={() =>
																				startEditing(
																					index,
																					"title",
																					link.title ||
																						extractTitleFromUrl(link.url),
																				)
																			}
																			title={t(
																				"editProfile.form.socialMedia.editTitle",
																			)}
																		>
																			<EditIcon
																				fill={theme.colors.defaultStrong}
																				width="16px"
																				height="16px"
																			/>
																		</Button>
																	</LinkValueWrapper>
																)}
															</LinkFieldWrapper>
														</LinkDetailsWrapper>
													</LinkContentWrapper>

													<DeleteLinkButton
														$variant="ghost"
														$size="small"
														// $fullWidth
														$iconOnly
														onClick={() => handleRemoveLink(index)}
														// style={{ borderRadius: "0px" }}
													>
														<DeleteOutlineIcon
															fill={theme.colors.defaultMain}
															width="16px"
															height="16px"
														/>

														{/* {t('form.socialMedia.removeLink')} */}
													</DeleteLinkButton>
												</SocialLinkItem>
											);
										})}
									</LinksList>
								)}
							</SocialMediaLinksRow>
						</InnerForm>

						<Actions>
							<Button
								type="button"
								$variant="outline"
								size="l"
								sizeTablet="l"
								sizeMobile="m"
								onClick={() => navigate(-1)}
							>
								{t("editProfile.cancel")}
							</Button>
							<Button
								type="submit"
								size="l"
								sizeTablet="l"
								sizeMobile="m"
								disabled={isLoading}
							>
								{isLoading ? t("editProfile.saving") : t("editProfile.save")}
							</Button>
						</Actions>
					</Form>
				</PersonalDetailsContainer>
			</EditProfileContainer>
		</>
	);
};

// Styled Components
const EditProfileContainer = styled.section`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  padding-top: 100px;
  padding-bottom: 120px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding-top: 0px;
    padding-bottom: 0px;
  }
`;

const InnerForm = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  max-width: 1400px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const Navigation = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultMain};
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  z-index: 1;
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
`;

const DeleteLinkButton = styled(Button)`
  border-radius: 0px;
  height: auto;
  width: 40px;
`;

const PersonalDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  max-width: 600px;
  border-radius: ${({ theme }) => theme.sizing.sm};
  background: ${({ theme }) => theme.fill.defaultMain};
  box-shadow: 0px 2px 12px 0px rgba(26, 26, 26, 0.04);
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    border-radius: 0px;
    box-shadow: none;
    border: none;
    padding-top: 80px;
  }
`;

const ActionsBlocks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const UserAvatarRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ImageWrapper = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
`;

const EmptyImageWrapper = styled(ImageWrapper)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  background: ${({ theme }) => theme.fill.defaultSubtle};
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadField = styled.label`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const UploadAvatarButton = styled.div`
  background-color: transparent;
  border: 1px solid ${theme.border.defaultWeak};
  color: ${theme.colors.defaultMain};
  transition: all 0.3s ease-in-out;
  gap: ${({ theme }) => theme.radius.xs};
  display: inline-flex;
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: ${({ theme }) => theme.radius.xs} 12px;
  cursor: pointer;
  align-items: center;

  &:hover {
    background-color: ${theme.palette.alabaster[100]};
  }
`;

const InputFile = styled.input`
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden !important;
  position: absolute;
  z-index: -1;
`;

const InputWrapper = styled.label`
  display: flex;
  flex-direction: column; 
  gap: 6px;
  flex-grow: 1;
  width: 100%;
`;

const NameInputWrapper = styled(InputWrapper)``;
const LastNameInputWrapper = styled(InputWrapper)``;
const PhonePrefixInputWrapper = styled(InputWrapper)``;
const PhoneInputWrapper = styled(InputWrapper)``;
const InputTextAreaWrapper = styled(InputWrapper)``;
const InputLinksWrapper = styled(InputWrapper)``;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.sizing.xs};
  }
`;

const PhonePrefixInput = styled.div`
  position: relative;
`;

const PrefixFlag = styled(CountryFlag)`
  position: absolute;
  top: 10px;
  left: 8px;
`;

const PrefixSelect = styled(Select)`
  padding-left: 36px;
  height: 40px;
`;

const SocialMediaLinksRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const LinksInputInnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.sizing.xs};
  }
`;

const LinksList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  width: 100%;
`;

const SocialLinkItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  justify-content: flex-start;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  box-sizing: border-box;
  overflow: hidden;
`;

const LinkContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  border-right: 1px solid ${({ theme }) => theme.border.defaultWeak};
`;

const LinkDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const LinkFieldWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  height: ${({ theme }) => theme.sizing.xl};
  min-height: 34px;
  padding-left: ${({ theme }) => theme.sizing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};

  &:last-child {
    border-bottom: none;
  }
`;
const LinkValue = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  flex: 1;
  min-width: 0;
`;

const LinkLabel = styled(Typography)`
  min-width: 40px;
`;

const LinkValueWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: 34px;
  height: ${({ theme }) => theme.sizing.xl};
  width: 100%;
  gap: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: space-between;
  }
`;

const LinkText = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  width: 100%;
  line-height: normal;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
  }

  // @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
  //   max-width: 100%;
  // }

  // @media (max-width: 360px) {
  //   max-width: 50%;
  // }
`;

const LinkTitle = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
  width: 100%;
  line-height: normal;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
  }

  // @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
  //   max-width: 60%;
  // }

  // @media (max-width: 360px) {
  //   max-width: 50%;
  // }
`;

const EditInputWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: ${({ theme }) => theme.sizing.xs};
  height: ${({ theme }) => theme.sizing.xl};
`;

const EditInput = styled(InputText)`
  padding: 0px;
  border: none;
  background: transparent;
  width: 100%;
  height: ${({ theme }) => theme.sizing.xl};
  min-height: 34px;
  
  &:focus {
    outline: none;
    border: none;
    background: transparent;
  }
`;

const ErrorMsg = styled.p`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: space-between;
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

export default EditProfile;
